import { CHANNELS } from "../shared/channels";
import { listen } from "./bridge";
import { readLook } from "./look";

// small celebrations and press feel: one pooled canvas of pixels and stars, a spring on release and a glow that follows the mouse.
// everything is skipped under calm motion or when the device switch is off, and nothing runs while idle

export type BurstKind = "task" | "routine" | "reminder" | "draw" | "medal";

const MAX = 90;
const COUNT: Record<BurstKind, number> = { task: 16, routine: 14, reminder: 10, draw: 30, medal: 44 };
const HUES: Record<BurstKind, string[]> = {
  task: ["--chi", "--green", "--amber"],
  routine: ["--green", "--chi"],
  reminder: ["--amber", "--chi"],
  draw: ["--pink", "--amber", "--chi", "--green"],
  medal: ["--amber", "--amber", "--chi", "--pink"]
};

interface Bit { on: boolean; x: number; y: number; vx: number; vy: number; life: number; age: number; size: number; star: boolean; color: string }

const pool: Bit[] = Array.from({ length: MAX }, () => ({ on: false, x: 0, y: 0, vx: 0, vy: 0, life: 1, age: 0, size: 4, star: false, color: "#fff" }));
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let frame = 0;
let last = 0;

function calm(): boolean {
  return document.documentElement.classList.contains("calm") || matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function juicy(): boolean {
  return !calm() && readLook().juice && document.visibilityState === "visible";
}

// a short buzz, only on phones that can and only when this device has vibration on
export function vibe(pattern: number | number[]): void {
  if (calm() || !readLook().haptics) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // no vibration here
  }
}

function stop(): void {
  cancelAnimationFrame(frame);
  frame = 0;
  pool.forEach((bit) => (bit.on = false));
  if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (canvas) canvas.hidden = true;
}

function tick(now: number): void {
  if (!canvas || !ctx) return;
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  let live = 0;
  for (const bit of pool) {
    if (!bit.on) continue;
    bit.age += dt;
    if (bit.age >= bit.life) {
      bit.on = false;
      continue;
    }
    live++;
    bit.vy += 520 * dt;
    bit.x += bit.vx * dt;
    bit.y += bit.vy * dt;
    ctx.globalAlpha = 1 - (bit.age / bit.life) ** 2;
    ctx.fillStyle = bit.color;
    const s = bit.size;
    const px = Math.round(bit.x);
    const py = Math.round(bit.y);
    if (bit.star) {
      ctx.fillRect(px - s, py - 1, s * 2, 2);
      ctx.fillRect(px - 1, py - s, 2, s * 2);
    } else ctx.fillRect(px, py, s, s);
  }
  if (live) frame = requestAnimationFrame(tick);
  else stop();
}

export function burst(x: number, y: number, kind: BurstKind): void {
  if (!juicy()) return;
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.className = "jx-canvas";
    canvas.setAttribute("aria-hidden", "true");
    ctx = canvas.getContext("2d");
    document.body.append(canvas);
  }
  if (canvas.width !== innerWidth || canvas.height !== innerHeight) {
    canvas.width = innerWidth;
    canvas.height = innerHeight;
  }
  canvas.hidden = false;
  const css = getComputedStyle(document.documentElement);
  const colors = HUES[kind].map((name) => css.getPropertyValue(name).trim() || "#fff");
  let made = 0;
  for (const bit of pool) {
    if (made >= COUNT[kind]) break;
    if (bit.on) continue;
    const angle = Math.random() * Math.PI * 2;
    const speed = (kind === "medal" ? 140 : 90) + Math.random() * 190;
    bit.on = true;
    bit.x = x;
    bit.y = y;
    bit.vx = Math.cos(angle) * speed;
    bit.vy = Math.sin(angle) * speed - 120;
    bit.age = 0;
    bit.life = 0.6 + Math.random() * 0.5;
    bit.size = 3 + Math.floor(Math.random() * 3);
    bit.star = Math.random() < 0.3;
    bit.color = colors[made % colors.length];
    made++;
  }
  if (!frame && made) {
    last = performance.now();
    frame = requestAnimationFrame(tick);
  }
}

function centre(element: Element): [number, number] {
  const box = element.getBoundingClientRect();
  return [box.left + box.width / 2, box.top + box.height / 2];
}

function pop(element: Element): void {
  element.classList.remove("jx-pop");
  void (element as HTMLElement).offsetWidth;
  element.classList.add("jx-pop");
  element.addEventListener("animationend", () => element.classList.remove("jx-pop"), { once: true });
}

const SPRING = "button:not(:disabled), .pill, .chip, .card, .tile, .sticky-note, .scrap";
const PRIMARY = ".pill.accent, button.primary, .pill.primary";

export function setupJuice(): void {
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !juicy()) return;
    const done = target.closest('[data-action="done"]');
    const check = target.closest("[data-action='check']");
    if (done) {
      burst(...centre(done), "task");
      pop(done);
      vibe([6, 30, 10]);
    } else if (check && !check.closest(".done")) {
      burst(...centre(check), "routine");
      pop(check);
      vibe([6, 30, 10]);
    }
  }, { capture: true, passive: true });

  document.addEventListener("submit", (event) => {
    const form = event.target as HTMLElement;
    if (form.id !== "reminders-form" || !juicy()) return;
    const key = form.querySelector('[type="submit"], button:not([type])') ?? form;
    burst(...centre(key), "reminder");
    vibe(8);
  }, { capture: true, passive: true });

  // press: a thin scan ring on primary keys, a spring back on release, a tick on switches
  document.addEventListener("pointerdown", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !juicy()) return;
    if (target.closest(PRIMARY) && readLook().tap === "off") {
      const ring = document.createElement("i");
      ring.className = "jx-scan";
      ring.style.left = `${event.clientX}px`;
      ring.style.top = `${event.clientY}px`;
      ring.addEventListener("animationend", () => ring.remove(), { once: true });
      document.body.append(ring);
    }
    if (target.closest(".toggle, .seg-option, .switch-option")) vibe(4);
    const key = target.closest(SPRING);
    if (!key) return;
    const release = () => {
      key.classList.remove("jx-rel");
      void (key as HTMLElement).offsetWidth;
      key.classList.add("jx-rel");
      key.addEventListener("animationend", () => key.classList.remove("jx-rel"), { once: true });
    };
    window.addEventListener("pointerup", release, { once: true });
  }, { passive: true });

  listen(CHANNELS.awardsUnlocked, () => {
    burst(innerWidth / 2, Math.min(120, innerHeight / 4), "medal");
    vibe([10, 40, 10, 40, 20]);
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") stop();
  });

  // a soft glow under a mouse, never on touch and never needed for anything
  if (matchMedia("(hover: hover) and (pointer: fine)").matches) {
    const glow = document.createElement("div");
    glow.className = "jx-glow";
    glow.setAttribute("aria-hidden", "true");
    document.body.append(glow);
    let queued = false;
    let gx = 0;
    let gy = 0;
    document.addEventListener("pointermove", (event) => {
      gx = event.clientX;
      gy = event.clientY;
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        glow.style.transform = `translate(${gx}px, ${gy}px)`;
        glow.classList.add("on");
      });
    }, { passive: true });
    document.documentElement.addEventListener("pointerleave", () => glow.classList.remove("on"));
  }
}
